import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PrivateRoutes from './components/PrivateRoutes';
import PublicRoutes from './components/PublicRoutes';
import AdminRoutes from './components/AdminRoutes';
import SuperAdminRouteGuard from './components/SuperAdminRouteGuard';
import OwnerAdminRouteGuard from './components/OwnerAdminRouteGuard';
import ClientRoutes from './components/ClientRoutes';
import StaffRouteGuard, { STAFF_HOME } from './components/StaffRouteGuard';
import Layout from './components/Layout';
import ClientLayout from './pages/clientPortal';
import { authentication } from './redux/actions';
import { STAFF_ROLES } from './redux/selectors';
import { ScreenshotModeProvider } from './context/ScreenshotModeContext';
import ScreenshotRandomizer from './components/ScreenshotRandomizer';
import 'bootstrap/dist/css/bootstrap.min.css';
import './theme.scss';
import './App.css';

const Stores = lazy(() => import('./pages/stores'));
const Users = lazy(() => import('./pages/users'));
const Clients = lazy(() => import('./pages/clients'));
const Login = lazy(() => import('./pages/login'));
const Details = lazy(() => import('./pages/details'));
const Store = lazy(() => import('./pages/store'));
const Account = lazy(() => import('./pages/account'));
const Products = lazy(() => import('./pages/products'));
const PasswordReset = lazy(() => import('./pages/passwordReset'));
const ForgotPassword = lazy(() => import('./pages/forgotPassword'));
const ResetPassword = lazy(() => import('./pages/resetPassword'));
const WebhookLogs = lazy(() => import('./pages/webhookLogs'));
const ClientDashboard = lazy(() => import('./pages/clientPortal/ClientDashboard'));
const ClientLedger = lazy(() => import('./pages/clientPortal/ClientLedger'));
const ClientInventory = lazy(() => import('./pages/clientPortal/ClientInventory'));
const ClientOrders = lazy(() => import('./pages/clientPortal/ClientOrders'));
const ClientOrderDetail = lazy(() => import('./pages/clientPortal/ClientOrderDetail'));
const ClientInvoices = lazy(() => import('./pages/clientPortal/ClientInvoices'));
const ClientInvoiceDetail = lazy(() => import('./pages/clientPortal/ClientInvoiceDetail'));
const AdminInvoices = lazy(() => import('./pages/adminInvoices'));
const AdminInvoiceDetail = lazy(() => import('./pages/adminInvoices/AdminInvoiceDetail'));
const AdminClientLedger = lazy(() => import('./pages/adminInvoices/AdminClientLedger'));
const AdminMasterLedger = lazy(() => import('./pages/adminInvoices/AdminMasterLedger'));
const AdminQuery = lazy(() => import('./pages/adminQuery'));
const AdminSearch = lazy(() => import('./pages/adminSearch'));
const ClientSearch = lazy(() => import('./pages/clientSearch'));
const AdminTools = lazy(() => import('./pages/adminTools'));
const AdminProductsPage = lazy(() => import('./pages/adminProducts'));
const AdminASNDetail = lazy(() => import('./pages/adminASN/AdminASNDetail'));
const AdminShipments = lazy(() => import('./pages/adminShipments'));
const AdminOrders = lazy(() => import('./pages/adminOrders'));
const AdminOrderDetail = lazy(() => import('./pages/adminOrders/AdminOrderDetail'));
const AdminTransfers = lazy(() => import('./pages/adminTransfers'));
const AdminTransferDetail = lazy(() => import('./pages/adminTransfers/AdminTransferDetail'));
const OwnerMyStore = lazy(() => import('./pages/ownerMyStore'));
const OwnerProducts = lazy(() => import('./pages/ownerProducts'));
const OwnerInventory = lazy(() => import('./pages/ownerInventory'));
const OwnerUsers = lazy(() => import('./pages/ownerUsers'));
const StoreOnboard = lazy(() => import('./pages/storeOnboard'));
const ClientShipments = lazy(() => import('./pages/clientPortal/ClientShipments'));
const ClientASNDetail = lazy(() => import('./pages/clientPortal/ClientASNDetail'));
const ClientASNNew = lazy(() => import('./pages/clientPortal/ClientASNNew'));
const ClientOutboundNew = lazy(() => import('./pages/clientPortal/ClientOutboundNew'));
const ClientOutboundDetail = lazy(() => import('./pages/clientPortal/ClientOutboundDetail'));
const AdminOutboundDetail = lazy(() => import('./pages/adminOutbound/AdminOutboundDetail'));
const ClientPurchases = lazy(() => import('./pages/clientPortal/ClientPurchases'));
const ClientTransfers = lazy(() => import('./pages/clientPortal/ClientTransfers'));
const ClientTransferDetail = lazy(() => import('./pages/clientPortal/ClientTransferDetail'));
const ClientAccount = lazy(() => import('./pages/clientPortal/ClientAccount'));
const Purchases = lazy(() => import('./pages/purchases'));
const PurchaseDetail = lazy(() => import('./pages/purchases/PurchaseDetail'));
const Dashboard = lazy(() => import('./pages/dashboard'));
const Help = lazy(() => import('./pages/help'));
const AdminCategoryDetail = lazy(() => import('./pages/adminCategories/AdminCategoryDetail'));
const AdminApiKeys = lazy(() => import('./pages/adminApiKeys'));
const AdminDocs = lazy(() => import('./pages/adminDocs'));
const FeeFlows = lazy(() => import('./pages/feeFlows'));
const AdminOccupancyPreview = lazy(() => import('./pages/adminOccupancyPreview'));
const AdminMissingShipmentCosts = lazy(() => import('./pages/adminMissingShipmentCosts'));

function RouteFallback() {
  return <div id="cover-spin"></div>;
}

// The Fulfillment Source Verification queue now lives as a tab inside the Store
// detail page. Redirect the legacy standalone route to the selected store's tab
// (or the stores list when no store is selected).
function FulfillmentAuditRedirect() {
  const selectedStore = useSelector((state) => state.stores?.selectedStore);
  const storeId = selectedStore?.storeId;
  return (
    <Navigate to={storeId ? `/stores/${storeId}?tab=source-verification` : '/stores'} replace />
  );
}

function App() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  useEffect(() => {
    dispatch(authentication());
  }, [dispatch]);

  if (user.loading) {
    return <div id="cover-spin"></div>;
  }

  const clientHome = '/client/dashboard';
  const adminHome = '/dashboard';
  const isClientView = user.role === 'client' || user.viewMode === 'client';
  const isStaff = STAFF_ROLES.includes(user.role);
  const staffHome = isStaff ? (STAFF_HOME[user.role] ?? '/orders') : null;

  const homeRedirect = isClientView ? clientHome : isStaff ? staffHome : adminHome;

  return (
    <ScreenshotModeProvider>
      <div className="app dark-mode">
        <ScreenshotRandomizer />
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Navigate to={homeRedirect} replace />} />

              <Route element={<PublicRoutes user={user} />}>
                <Route path="/login" element={<Login />} />
              </Route>

              {/* Standalone, no auth guard: /reset-password must work for both
                anonymous users (emailed link) and already-authenticated ones. */}
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              <Route element={<PrivateRoutes user={user} />}>
                <Route element={<AdminRoutes user={user} />}>
                  <Route
                    element={
                      <StaffRouteGuard user={user}>
                        <Layout />
                      </StaffRouteGuard>
                    }>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/stores">
                      <Route index element={<Stores />} />
                      <Route path="onboard" element={<StoreOnboard />} />
                      <Route path=":storeId" element={<Store />} />
                      <Route path=":storeId/webhook-logs" element={<WebhookLogs />} />
                    </Route>
                    <Route path="/clients">
                      <Route index element={<Clients />} />
                      <Route path=":clientId" element={<Details />} />
                    </Route>
                    <Route path="/invoices">
                      <Route index element={<AdminInvoices />} />
                      <Route path=":invoiceId" element={<AdminInvoiceDetail />} />
                    </Route>
                    <Route path="/ledger" element={<AdminMasterLedger />} />
                    <Route path="/clients/:clientId/ledger" element={<AdminClientLedger />} />
                    <Route path="/product/:productId" element={<Products />} />
                    <Route path="/users" element={<Users />} />
                    <Route path="/account" element={<Account />} />
                    <Route path="/tools" element={<AdminTools />} />
                    <Route path="/webhooks" element={<Navigate to="/tools" replace />} />
                    <Route path="/events" element={<Navigate to="/tools#events" replace />} />
                    <Route
                      path="/bulk-owner"
                      element={<Navigate to="/tools#bulk-owner" replace />}
                    />
                    <Route path="/query" element={<AdminQuery />} />
                    <Route path="/search" element={<AdminSearch />} />
                    <Route path="/admin/products" element={<AdminProductsPage />} />
                    <Route path="/asn">
                      <Route index element={<Navigate to="/shipments" replace />} />
                      <Route path=":asnId" element={<AdminASNDetail />} />
                    </Route>
                    <Route path="/outbound">
                      <Route index element={<Navigate to="/shipments?tab=outbound" replace />} />
                      <Route path=":id" element={<AdminOutboundDetail />} />
                    </Route>
                    <Route path="/shipments" element={<AdminShipments />} />
                    <Route path="/orders">
                      <Route index element={<AdminOrders />} />
                      <Route path=":orderId" element={<AdminOrderDetail />} />
                    </Route>
                    <Route path="/transfers">
                      <Route index element={<AdminTransfers />} />
                      <Route path=":transferOrderId" element={<AdminTransferDetail />} />
                    </Route>
                    <Route path="/purchases">
                      <Route index element={<Purchases />} />
                      <Route path=":vendorOrderId" element={<PurchaseDetail />} />
                    </Route>
                    <Route path="/my-store" element={<OwnerMyStore />} />
                    <Route path="/owner/products" element={<OwnerProducts />} />
                    <Route path="/owner/inventory" element={<OwnerInventory />} />
                    <Route path="/my-clients" element={<Clients title="My Clients" />} />
                    <Route path="/owner/users" element={<OwnerUsers />} />
                    <Route
                      path="/product-categories/:categoryId"
                      element={<AdminCategoryDetail />}
                    />
                    <Route path="/help" element={<Help />} />
                    <Route path="/help/:slug" element={<Help />} />
                    <Route path="/api-keys" element={<AdminApiKeys />} />
                    <Route path="/docs" element={<AdminDocs />} />
                    <Route path="/fee-flows" element={<FeeFlows />} />
                    <Route
                      path="/admin/billing/occupancy-preview"
                      element={
                        <SuperAdminRouteGuard user={user}>
                          <AdminOccupancyPreview />
                        </SuperAdminRouteGuard>
                      }
                    />
                    <Route
                      path="/admin/billing/missing-shipment-costs"
                      element={
                        <OwnerAdminRouteGuard user={user}>
                          <AdminMissingShipmentCosts />
                        </OwnerAdminRouteGuard>
                      }
                    />
                    <Route
                      path="/billing/fulfillment-audit"
                      element={<FulfillmentAuditRedirect />}
                    />
                  </Route>
                </Route>

                <Route element={<ClientRoutes user={user} />}>
                  <Route element={<ClientLayout />}>
                    <Route
                      path="/client"
                      index
                      element={<Navigate to="/client/dashboard" replace />}
                    />
                    <Route path="/client/dashboard" element={<ClientDashboard />} />
                    <Route path="/client/ledger" element={<ClientLedger />} />
                    <Route path="/client/inventory" element={<ClientInventory />} />
                    <Route path="/client/orders" element={<ClientOrders />} />
                    <Route path="/client/orders/:orderId" element={<ClientOrderDetail />} />
                    <Route path="/client/invoices" element={<ClientInvoices />} />
                    <Route path="/client/invoices/:invoiceId" element={<ClientInvoiceDetail />} />
                    <Route
                      path="/client/products"
                      element={<Navigate to="/client/inventory" replace />}
                    />
                    <Route path="/client/purchases" element={<ClientPurchases />} />
                    <Route path="/client/shipments" element={<ClientShipments />} />
                    {/* Legacy deep links: list pages redirect into the combined
                      Orders view; wizard + detail routes stay unchanged. */}
                    <Route path="/client/asn">
                      <Route index element={<Navigate to="/client/shipments" replace />} />
                      <Route path="new" element={<ClientASNNew />} />
                      <Route path=":asnId" element={<ClientASNDetail />} />
                    </Route>
                    <Route path="/client/outbound">
                      <Route
                        index
                        element={<Navigate to="/client/shipments?tab=outbound" replace />}
                      />
                      <Route path="new" element={<ClientOutboundNew />} />
                      <Route path=":id" element={<ClientOutboundDetail />} />
                    </Route>
                    <Route path="/client/transfers">
                      <Route index element={<ClientTransfers />} />
                      <Route path=":transferOrderId" element={<ClientTransferDetail />} />
                    </Route>
                    <Route path="/client/account" element={<ClientAccount />} />
                    <Route path="/client/search" element={<ClientSearch />} />
                    <Route path="/client/query" element={<AdminQuery portal="client" />} />
                    <Route path="/client/help" element={<Help portal="client" />} />
                    <Route path="/client/help/:slug" element={<Help portal="client" />} />
                  </Route>
                </Route>

                <Route path="/password-reset" element={<PasswordReset />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </ScreenshotModeProvider>
  );
}

export default App;
